import React, { ComponentType, LazyExoticComponent } from 'react';
import ChunkErrorBoundary from '../components/ChunkErrorBoundary';

// HOC to wrap lazy components with error boundary
export function withChunkErrorBoundary<P extends object>(
  LazyComponent: LazyExoticComponent<ComponentType<P>>,
  fallbackComponent?: React.ComponentType
) {
  const WrappedComponent = (props: P) => {
    const FallbackComponent = fallbackComponent;

    return (
      <ChunkErrorBoundary
        fallback={
          FallbackComponent ? (
            <FallbackComponent />
          ) : (
            <div className="min-h-screen bg-dark-950 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-green mx-auto mb-4"></div>
                <p className="text-gray-400">Loading component...</p>
              </div>
            </div>
          )
        }
        onRetry={() => {
          // Force component to re-mount
          window.location.reload();
        }}
      >
        <LazyComponent {...(props as any)} />
      </ChunkErrorBoundary>
    );
  };

  WrappedComponent.displayName = `withChunkErrorBoundary(${(LazyComponent as any).displayName || 'LazyComponent'})`;

  return WrappedComponent;
}

// Utility function to create lazy component with automatic error boundary
export function createLazyComponent<P extends object>(
  importFn: () => Promise<{ default: ComponentType<P> }>,
  fallbackComponent?: React.ComponentType
) {
  const LazyComponent = React.lazy(importFn);
  return withChunkErrorBoundary(LazyComponent, fallbackComponent);
}

// Hook to handle chunk loading errors
export function useChunkErrorHandler() {
  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      // Check if it's a chunk loading error
      if (
        error &&
        typeof error === 'object' &&
        (error.message?.includes('Loading chunk') ||
         error.message?.includes('Loading CSS chunk') ||
         error.name === 'ChunkLoadError')
      ) {
        console.warn('Chunk loading error detected, attempting recovery...');

        // Prevent the default unhandled rejection behavior
        event.preventDefault();

        // Attempt to reload the page after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
}